import { ListSubheading, List, Spinner } from '@oliasoft-open-source/react-ui-library';
import { useTranslation } from 'react-i18next';
import translations from 'src/internationalisation/translation-map.json';
import { connect, ConnectedProps } from 'react-redux';
import {
  rigOperationTemplatesItemSelected,
  addRigOperationTemplate,
  removeRigOperationTemplateItem,
  updateRigOperationTemplateItem,
} from 'src/store/entities/company-settings/company-settings';
import { IRigOperationTemplate } from 'src/common/interfaces/rig-operations-template.interfaces';
import type { TRootState } from 'src/store/store-types';
import { convertToKebabCase } from 'client/common/utils/string-utils';

interface RigOperationRenameModalProps {
  visible: boolean;
  rename: boolean;
}
interface RigOperationTemplateListProps extends PropsFromRedux {
  rigOperationTemplates: IRigOperationTemplate[];
  setRigOperationRenameModalVisible: (props: RigOperationRenameModalProps) => void;
}

const RigOperationTemplatesList = ({
  rigOperationTemplates,
  activeRigOperationTemplateId,
  rigOperationTemplatesItemSelected,
  isAdding,
  removeRigOperationTemplateItem,
  setRigOperationRenameModalVisible,
}: RigOperationTemplateListProps) => {
  const { t } = useTranslation();
  const rigOperationTemplatesList =
    rigOperationTemplates?.map((rigOperationTemplate) => ({
      id: rigOperationTemplate.rigOperationTemplateId,
      name: rigOperationTemplate.name,
      active: rigOperationTemplate.rigOperationTemplateId === activeRigOperationTemplateId,
      onClick: () => rigOperationTemplatesItemSelected(rigOperationTemplate.rigOperationTemplateId),
      testId: `rig-operation-templates-list-${convertToKebabCase(rigOperationTemplate.name)}-item`,
      actions: [
        {
          label: 'More',
          testId: `rig-operation-templates-list-${convertToKebabCase(rigOperationTemplate.name)}-more-button`,
          subActions: [
            {
              icon: 'delete',
              label: t(translations.delete),
              testId: `rig-operation-templates-list-${convertToKebabCase(rigOperationTemplate.name)}-delete-button`,
            },
            {
              icon: 'edit',
              label: t(translations.rename),
              testId: `rig-operation-templates-list-${convertToKebabCase(rigOperationTemplate.name)}-rename-button`,
            },
          ],
        },
      ],
    })) || [];
  return (
    <>
      <ListSubheading
        item={{
          testId: 'rig-operation-templates-list-subheading',
          name: t(translations.rigOperationTemplates),
          actions: [
            {
              label: 'Add',
              icon: isAdding ? <Spinner tiny dark /> : 'add',
              disabled: isAdding,
              primary: true,
              testId: 'rig-operation-templates-list-subheading-more-button',
              onClick: () => setRigOperationRenameModalVisible({ visible: true, rename: false }),
            },
          ],
        }}
      />
      <List
        testId="rig-operation-templates-list"
        list={{
          items: rigOperationTemplatesList?.map((template) => ({
            ...template,
            actions: template.actions.map((action) => ({
              ...action,
              subActions: action.subActions.map((subAction) => ({
                ...subAction,
                onClick: () => {
                  if (subAction.icon === 'delete') {
                    removeRigOperationTemplateItem(template.id);
                  }
                  if (subAction.icon === 'edit') {
                    rigOperationTemplatesItemSelected(template.id);
                    setRigOperationRenameModalVisible({ visible: true, rename: true });
                  }
                },
              })),
            })),
          })),
        }}
        noHeader
      />
    </>
  );
};
const mapStateToProps = ({ entities }: TRootState) => {
  const { activeRigOperationTemplateId, isFetching, isAdding } = entities.companySettings;

  return {
    isFetching,
    activeRigOperationTemplateId,
    isAdding,
  };
};

const mapDispatchToProps = {
  rigOperationTemplatesItemSelected,
  addRigOperationTemplate,
  updateRigOperationTemplateItem,
  removeRigOperationTemplateItem,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = connector(RigOperationTemplatesList);

export { Container as RigOperationTemplatesList };
